import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTheme, useMediaQuery, Drawer, IconButton, Typography, Button, Fab, alpha, Collapse } from '@mui/material';
import History from '@mui/icons-material/History';
import Close from '@mui/icons-material/Close';
import DeleteSweep from '@mui/icons-material/DeleteSweep';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import { useSelector, useDispatch } from 'react-redux';
import { clearApiHistory } from 'store/slices/apiHistory.slice';
import AppBoxComponent from 'components/mui/AppBoxComponent';
import AppTypographyComponent from 'components/mui/AppTypographyComponent';

/**
 * Mobile-only: floating button to open API call history drawer.
 * Drawer lists recent API calls (method, url, status, response, time).
 * Visibility can be toggled via prop `enabled` or env VITE_SHOW_API_HISTORY (true/false).
 */
const ApiHistoryDrawer = ({ showOnMobileOnly = true, enabled: enabledProp }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const entries = useSelector((state) => state.apiHistory?.entries ?? []);

  const handleClear = useCallback(() => {
    dispatch(clearApiHistory());
    setExpandedId(null);
  }, [dispatch]);

  const toggleExpanded = useCallback((id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const enabledByEnv = import.meta.env.VITE_SHOW_API_HISTORY !== 'false';
  const enabled = enabledProp !== undefined ? Boolean(enabledProp) : enabledByEnv;

  if (!enabled) {
    return null;
  }
  if (!showOnMobileOnly || !isMobile) {
    return null;
  }

  return (
    <>
      <Fab
        onClick={() => setOpen(true)}
        aria-label="Show API History"
        size="medium"
        sx={{
          position: 'fixed',
          bottom: theme.spacing(10),
          left: theme.spacing(2),
          zIndex: 9999,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          boxShadow: theme.shadows[6],
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: theme.shadows[8],
          },
        }}
      >
        <History />
      </Fab>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            maxHeight: '70vh',
            borderTopLeftRadius: theme.spacing(2),
            borderTopRightRadius: theme.spacing(2),
            backgroundColor: theme.palette.custom?.background?.page || theme.palette.grey[900],
          },
        }}
      >
        <AppBoxComponent
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 200,
          }}
        >
          <AppBoxComponent
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              pb: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <AppTypographyComponent sx={{ fontWeight: 600, fontSize: '1rem' }}>
              API Call History
            </AppTypographyComponent>
            <AppBoxComponent sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                size="small"
                startIcon={<DeleteSweep />}
                onClick={handleClear}
                disabled={entries.length === 0}
                sx={{ textTransform: 'none', mr: 0.5 }}
              >
                Clear
              </Button>
              <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close">
                <Close />
              </IconButton>
            </AppBoxComponent>
          </AppBoxComponent>

          <AppBoxComponent
            sx={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            {entries.length === 0 ? (
              <AppTypographyComponent
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.875rem',
                  textAlign: 'center',
                  py: 3,
                }}
              >
                No API calls yet
              </AppTypographyComponent>
            ) : (
              entries.map((entry) => {
                const isExpanded = expandedId === entry.id;
                const hasResponseBody = entry.responseBody != null && String(entry.responseBody).trim() !== '';
                return (
                  <AppBoxComponent
                    key={entry.id}
                    onClick={() => hasResponseBody && toggleExpanded(entry.id)}
                    sx={{
                      py: 1,
                      px: 1.5,
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.common.white, 0.04),
                      borderLeft: `3px solid ${
                        entry.status >= 200 && entry.status < 300
                          ? theme.palette.success.main
                          : entry.status >= 400
                            ? theme.palette.error.main
                            : theme.palette.primary.main
                      }`,
                      cursor: hasResponseBody ? 'pointer' : 'default',
                    }}
                  >
                    <AppBoxComponent
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          color: theme.palette.primary.main,
                        }}
                      >
                        {entry.method}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '0.7rem',
                          color:
                            entry.status >= 200 && entry.status < 300
                              ? theme.palette.success.main
                              : entry.status >= 400
                                ? theme.palette.error.main
                                : theme.palette.text.secondary,
                        }}
                      >
                        {entry.status}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{ fontSize: '0.7rem', color: theme.palette.text.secondary }}
                      >
                        {entry.time}
                      </Typography>
                      {hasResponseBody && (
                        <IconButton
                          size="small"
                          sx={{ p: 0, ml: 'auto' }}
                          aria-label={isExpanded ? 'Collapse response' : 'Show response'}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(entry.id);
                          }}
                        >
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      )}
                    </AppBoxComponent>
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        color: theme.palette.text.primary,
                        wordBreak: 'break-all',
                        mt: 0.25,
                      }}
                    >
                      {entry.url}
                    </Typography>
                    {entry.response != null && entry.response !== undefined && (
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          color: theme.palette.text.secondary,
                          mt: 0.25,
                          fontStyle: 'italic',
                        }}
                      >
                        Response: {String(entry.response)}
                      </Typography>
                    )}
                    <Collapse in={isExpanded} timeout="auto">
                      {hasResponseBody && (
                        <AppBoxComponent
                          component="pre"
                          sx={{
                            mt: 1,
                            p: 1,
                            fontSize: '0.65rem',
                            fontFamily: 'monospace',
                            overflow: 'auto',
                            maxHeight: 200,
                            backgroundColor: alpha(theme.palette.common.black, 0.3),
                            borderRadius: 1,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {String(entry.responseBody)}
                        </AppBoxComponent>
                      )}
                    </Collapse>
                  </AppBoxComponent>
                );
              })
            )}
          </AppBoxComponent>
        </AppBoxComponent>
      </Drawer>
    </>
  );
};

ApiHistoryDrawer.propTypes = {
  showOnMobileOnly: PropTypes.bool,
  /** When false, button and drawer are hidden. When true, shown (if mobile). When undefined, uses VITE_SHOW_API_HISTORY env (default true). */
  enabled: PropTypes.bool,
};

export default ApiHistoryDrawer;
